import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    if (!orderId) { setState("error"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/${orderId}/status`);
        if (cancelled) return;
        if (data.order_status === "PAID") {
          setUser(data.user);
          setState("paid");
          toast.success("Payment successful! Your account is now active.");
          return;
        }
        if (["FAILED", "EXPIRED", "CANCELLED", "TERMINATED"].includes(data.order_status)) {
          setState("failed");
          return;
        }
        tries.current += 1;
        if (tries.current < 12) setTimeout(poll, 2500);
        else setState("pending");
      } catch (e) {
        if (!cancelled) { toast.error(errMsg(e)); setState("error"); }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [orderId, setUser]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <AppHeader />
      <main className="px-6 max-w-xl mx-auto py-20 text-center" data-testid="payment-result-page">
        {state === "checking" && (
          <div className="fade-up">
            <Loader2 className="h-10 w-10 animate-spin text-slate-400 mx-auto" />
            <h1 className="font-heading text-2xl font-semibold mt-6">Confirming your payment…</h1>
            <p className="text-slate-500 mt-2 text-sm">Hang tight — this usually takes a few seconds.</p>
          </div>
        )}
        {state === "paid" && (
          <div className="fade-up" data-testid="payment-result-paid">
            <div className="h-16 w-16 rounded-full bg-green-100 grid place-items-center mx-auto">
              <CheckCircle2 className="h-9 w-9 text-green-700" />
            </div>
            <h1 className="font-heading text-3xl font-bold mt-6">Your account is active!</h1>
            <p className="text-slate-500 mt-2">Thanks for joining Pinslib Mock Tests. All mock tests are now unlocked.</p>
            <Button onClick={() => nav("/dashboard")} data-testid="payment-result-continue" className="mt-8 bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white text-white">Go to dashboard</Button>
          </div>
        )}
        {state === "pending" && (
          <div className="fade-up" data-testid="payment-result-pending">
            <div className="h-16 w-16 rounded-full bg-amber-100 grid place-items-center mx-auto">
              <Clock className="h-9 w-9 text-amber-700" />
            </div>
            <h1 className="font-heading text-3xl font-bold mt-6">Payment is being processed</h1>
            <p className="text-slate-500 mt-2">We're still confirming with the payment gateway. Your dashboard will unlock the moment it's confirmed — refresh in a minute.</p>
            <Button onClick={() => nav("/dashboard")} className="mt-8 bg-slate-900 hover:bg-[#2563EB] text-white">Back to dashboard</Button>
          </div>
        )}
        {state === "failed" && (
          <div className="fade-up" data-testid="payment-result-failed">
            <div className="h-16 w-16 rounded-full bg-red-100 grid place-items-center mx-auto">
              <XCircle className="h-9 w-9 text-red-700" />
            </div>
            <h1 className="font-heading text-3xl font-bold mt-6">Payment did not go through</h1>
            <p className="text-slate-500 mt-2">No money was taken. Please try again from the dashboard.</p>
            <Button onClick={() => nav("/dashboard")} data-testid="payment-result-retry" className="mt-8 bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white text-white">Return to dashboard</Button>
          </div>
        )}
        {state === "error" && (
          <div className="fade-up" data-testid="payment-result-error">
            <h1 className="font-heading text-2xl font-semibold">Something went wrong</h1>
            <p className="text-slate-500 mt-2 text-sm">We couldn't confirm this payment just now. Head back to the dashboard and try again.</p>
            <Button onClick={() => nav("/dashboard")} className="mt-6">Back</Button>
          </div>
        )}
      </main>
    </div>
  );
}
